import { DocumentNode, parse } from "graphql";
import { DynamicManager, BasicState, CustomState } from "../common/DynamicManager";
import { EMPTY_VALUE, FieldName, FieldSchema, Schema } from "../Schema";


export interface GraphqlSchema {
    selection?: string;
}

/**
 * The fields a record is read and written with.
 *
 * A field opts out by declaring `selection: undefined`; everything else is selected under its
 * own name. This is the one place that answers "which attributes make up the record", so a
 * caller that fetches a record and a caller that sends one cannot disagree about it — and
 * they must not: the proposed-change diff walks the union of both sides' keys, so a field one
 * side omits is reported as created or deleted rather than ignored.
 *
 * @param schema - The schema describing the record.
 * @returns The field names, in schema order.
 */
export const getSelectedFieldNames = (schema: Schema): FieldName[] =>
  Object.values(schema)
    .filter((field) => (
      field.graphql && Object.prototype.hasOwnProperty.call(field.graphql, "selection") ?
        Boolean(field.graphql.selection) :
        true
    ))
    .map((field) => field.name);

export interface GraphqlFieldState extends BasicState {
  selection?: string;
}

export interface GraphqlState extends CustomState {
  selectionFragment?: DocumentNode | null;
}

export const defaultGraphqlState: GraphqlState = {
  loading: true,
  selectionFragment: EMPTY_VALUE,
}


export class GraphqlManager extends DynamicManager<GraphqlFieldState, GraphqlState> {

  customState: GraphqlState = defaultGraphqlState;

  mainType: string = "DigitalLabInstrumentRepositoryEntry";

  fragmentName: string = "Selection";

  constructor(
    schema: Schema = {}
  ) {
    super(schema);
  }

  updateCustomState(): void {
    this.customState.selectionFragment = parse(`
      fragment ${this.fragmentName} on ${this.mainType} {
        ${Object.values(this.state).map(
          fieldState => fieldState.selection
        ).filter(Boolean).join("\n")}
      }
    `);
  }

  getMapping(field: FieldSchema): Partial<Record<keyof GraphqlFieldState, any>> {
    return {
      selection: field.graphql?.hasOwnProperty("selection") ?
        field.graphql.selection :
        field.name,
    };
  }
  
}